import React, { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Button } from '@/components/ui/button';
import { BarChart3, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';

// iter91b: mini boundary specifico per il grafico — se recharts crasha
// (SVG malformato / dati anomali) il resto della pagina resta usabile.
class ChartErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ChartErrorBoundary]', error, info);
    try {
      const url = process.env.REACT_APP_BACKEND_URL;
      if (url) {
        fetch(`${url}/api/client-errors`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: `[ChartErrorBoundary] ${String(error?.message || error)}`,
            stack: String(error?.stack || ''),
            component_stack: String(info?.componentStack || ''),
            location: window.location.href, user_agent: navigator.userAgent,
            timestamp: new Date().toISOString(),
          }),
        }).catch(() => {});
      }
    } catch (_) { /* silent */ }
  }
  render() {
    if (this.state.err) {
      return (
        <div className="px-3 py-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2 m-3">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Impossibile mostrare il grafico</div>
            <div className="text-amber-700 mt-0.5">Il resto della pagina funziona normalmente. Riprova più tardi o segnala il problema.</div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * AndamentoMensileFatture (iter88)
 *
 * Mini bar chart mensile che mostra due serie affiancate:
 *   - "Pagate":    importo totale delle fatture pagate/incassate del mese
 *   - "Da pagare": importo totale delle fatture in attesa (include scadute)
 *
 * Utilizzabile sia per Fatture Emesse (Uscita) che Ricevute (Entrata).
 * La classificazione dello stato è delegata al chiamante tramite `classifyStato`.
 *
 * Props:
 *   - fatture: array di fatture
 *   - classifyStato(f): ritorna 'pagata' | 'da_pagare' | null (null = esclusa)
 *   - amountOf(f): ritorna importo numerico (default: Number(f.importo)||0)
 *   - defaultOpen: booleano, default false
 *   - mesiIndietro: numero, default 12
 *   - testIdPrefix: string, es. "fatture-uscita" / "fatture-entrata"
 *   - accentColor: 'emerald' | 'blue'  (colore della barra "Pagate")
 */
const MONTH_LABELS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const formatEuro = (v) => `€ ${(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const formatEuroFull = (v) => `€ ${(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-md p-3 text-xs">
      <div className="font-semibold text-slate-900 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 mt-0.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />
          <span className="text-slate-600">{p.name}:</span>
          <span className="font-semibold text-slate-900">{formatEuroFull(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export function AndamentoMensileFatture({
  fatture = [],
  classifyStato,
  amountOf,
  defaultOpen = false,
  mesiIndietro = 12,
  testIdPrefix = 'andamento',
  accentColor = 'emerald',
}) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(`${testIdPrefix}-andamento-open`);
      if (saved != null) return saved === '1';
    } catch (e) { /* ignore */ }
    return defaultOpen;
  });

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(`${testIdPrefix}-andamento-open`, next ? '1' : '0'); } catch (e) { /* ignore */ }
  };

  const chartData = useMemo(() => {
    // Costruisce le ultime N mensilità (chiave YYYY-MM), fino al mese corrente.
    const now = new Date();
    const buckets = [];
    for (let i = mesiIndietro - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const y = d.getFullYear();
      const m = d.getMonth();
      const key = `${y}-${String(m + 1).padStart(2, '0')}`;
      buckets.push({
        key,
        label: `${MONTH_LABELS[m]} ${String(y).slice(-2)}`,
        pagate: 0,
        daPagare: 0,
      });
    }
    const byKey = new Map(buckets.map((b) => [b.key, b]));

    const getAmt = amountOf || ((f) => Number(f?.importo) || 0);
    const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;
    for (const f of fatture) {
      if (!f || typeof f.data !== 'string' || !ISO_DATE_RE.test(f.data)) continue;
      const key = f.data.slice(0, 7); // YYYY-MM
      const bucket = byKey.get(key);
      if (!bucket) continue;
      const stato = classifyStato ? classifyStato(f) : null;
      if (!stato) continue;
      const imp = getAmt(f);
      if (stato === 'pagata') bucket.pagate += imp;
      else if (stato === 'da_pagare') bucket.daPagare += imp;
    }
    return buckets;
  }, [fatture, classifyStato, amountOf, mesiIndietro]);

  const totali = useMemo(() => {
    const tp = chartData.reduce((s, b) => s + b.pagate, 0);
    const tdp = chartData.reduce((s, b) => s + b.daPagare, 0);
    return { pagate: tp, daPagare: tdp };
  }, [chartData]);

  const isEmpty = totali.pagate === 0 && totali.daPagare === 0;
  const pagateColor = accentColor === 'blue' ? '#3b82f6' : '#10b981';
  const daPagareColor = '#f59e0b';

  return (
    <div className="border-b border-slate-100 bg-white" data-testid={`${testIdPrefix}-andamento-wrapper`}>
      <div className="px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <BarChart3 className="h-3.5 w-3.5 text-slate-500" />
          <span className="font-semibold">Andamento ultimi {mesiIndietro} mesi</span>
          {!isEmpty && (
            <>
              <span className="hidden sm:inline text-slate-400">·</span>
              <span className="hidden sm:inline">
                <span className="font-medium" style={{ color: pagateColor }}>Pagate {formatEuro(totali.pagate)}</span>
                <span className="mx-1.5 text-slate-300">·</span>
                <span className="font-medium" style={{ color: daPagareColor }}>Da pagare {formatEuro(totali.daPagare)}</span>
              </span>
            </>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={toggleOpen}
          className="h-7 text-xs"
          data-testid={`${testIdPrefix}-andamento-toggle`}
        >
          {open ? <><ChevronUp className="h-3.5 w-3.5 mr-1" />Nascondi</> : <><ChevronDown className="h-3.5 w-3.5 mr-1" />Mostra grafico</>}
        </Button>
      </div>
      {open && (
        <div className="px-3 pb-3" data-testid={`${testIdPrefix}-andamento-chart`}>
          {isEmpty ? (
            <div className="text-center text-slate-400 text-xs py-8 border border-dashed border-slate-200 rounded-md">
              Nessuna fattura negli ultimi {mesiIndietro} mesi
            </div>
          ) : (
            <ChartErrorBoundary>
              <div style={{ width: '100%', height: 200 }}>
              <ResponsiveContainer>
                <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis
                    tick={{ fontSize: 10, fill: '#64748b' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                    tickLine={false}
                    tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
                    width={44}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }} />
                  <Legend
                    wrapperStyle={{ fontSize: 11 }}
                    iconType="square"
                    iconSize={10}
                  />
                  <Bar dataKey="pagate" name="Pagate" fill={pagateColor} radius={[4, 4, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="daPagare" name="Da pagare" fill={daPagareColor} radius={[4, 4, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            </ChartErrorBoundary>
          )}
        </div>
      )}
    </div>
  );
}

export default AndamentoMensileFatture;
